import { PayloadAction } from '@reduxjs/toolkit'

import { LoadingStatusEnum } from '../../../../store/types'
import {
  CommissionBenchmarkAccountBreakdownResponse,
  CommissionBenchmarkAccountOverviewResponse,
  CommissionBenchmarkReferenceResponse,
} from '../api/types'

import { createDefaultDetailState, StateType } from './initialState'

export const GetCommissionBenchmarkReferenceReducer = {
  pending: (state: StateType) => {
    state.referenceLoading = LoadingStatusEnum.Pending
    state.referenceError = null
  },
  fulfilled: (state: StateType, action: PayloadAction<CommissionBenchmarkReferenceResponse>) => {
    state.referenceLoading = LoadingStatusEnum.Succeeded
    state.reference = action.payload
    state.referenceError = null
  },
  rejected: (state: StateType, action: PayloadAction<string | undefined>) => {
    state.referenceLoading = LoadingStatusEnum.Failed
    state.reference = null
    state.referenceError = action.payload ?? null
  },
}

export const GetCommissionBenchmarkOverviewReducer = {
  pending: (state: StateType, action: PayloadAction<unknown, string, { requestId: string }>) => {
    state.overviewLoading = LoadingStatusEnum.Pending
    state.overviewError = null
    state.overviewRequestId = action.meta.requestId
  },
  fulfilled: (
    state: StateType,
    action: PayloadAction<
      CommissionBenchmarkAccountOverviewResponse,
      string,
      { requestId: string }
    >,
  ) => {
    if (state.overviewRequestId && state.overviewRequestId !== action.meta.requestId) {
      return
    }
    state.overviewLoading = LoadingStatusEnum.Succeeded
    state.overview = action.payload.data.accounts
    state.overviewPagination = {
      ...state.overviewPagination,
      ...action.payload.data.meta,
    }
    state.overviewError = null
    state.overviewRequestId = null
  },
  rejected: (
    state: StateType,
    action: PayloadAction<string | undefined, string, { requestId: string }>,
  ) => {
    if (state.overviewRequestId && state.overviewRequestId !== action.meta.requestId) {
      return
    }
    state.overviewLoading = LoadingStatusEnum.Failed
    state.overview = []
    state.overviewError = action.payload ?? null
    state.overviewRequestId = null
  },
}

type AccountBreakdownPayload = {
  accountId: number
  response: CommissionBenchmarkAccountBreakdownResponse
}

type AccountBreakdownParams = {
  accountId: number
}

export const GetCommissionBenchmarkAccountBreakdownReducer = {
  pending: (
    state: StateType,
    action: PayloadAction<unknown, string, { arg: AccountBreakdownParams }>,
  ) => {
    const accountId = action.meta.arg.accountId
    const current = state.details[accountId] ?? createDefaultDetailState()

    state.details[accountId] = {
      ...current,
      loading: LoadingStatusEnum.Pending,
      error: null,
    }
  },
  fulfilled: (state: StateType, action: PayloadAction<AccountBreakdownPayload>) => {
    const { accountId, response } = action.payload
    const current = state.details[accountId] ?? createDefaultDetailState()

    state.details[accountId] = {
      ...current,
      loading: LoadingStatusEnum.Succeeded,
      error: null,
      loaded: true,
      data: response.data.symbols ?? [],
    }
  },
  rejected: (
    state: StateType,
    action: PayloadAction<string | undefined, string, { arg: AccountBreakdownParams }>,
  ) => {
    const accountId = action.meta.arg.accountId
    const current = state.details[accountId] ?? createDefaultDetailState()

    state.details[accountId] = {
      ...current,
      loading: LoadingStatusEnum.Failed,
      error: action.payload ?? null,
    }
  },
}
